import { Stack } from "@mui/material";
import type { FC } from "react";
import type { ClientPortfolioCryptocurrencyInstructionResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	ClientPortfolioInvestmentEnterFeeAmountTypeEnum,
	ClientPortfolioInvestmentEnterFeeTypeEnum,
	ClientPortfolioInvestmentFrequencyEnum,
	ClientPortfolioInvestmentTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { getCurrencySymbol } from "web_myplann/Infrastructure/Utils/CurrencyUtils";
import { getIsActive } from "web_myplann/Portfolio/Helpers/utils";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import { DataRowCollapsible } from "web_myplann/Shared/Components/DataRowCollapsible";
import type { CurrencyCode } from "web_myplann/Shared/constants";
import {
	dateFormatter,
	numberFormatter,
} from "web_myplann/Shared/Utils/formatters";
import { EMPTY_DETAIL_FIELD } from "web_myplann/Utils/constants";

type Props = {
	crypto: ClientPortfolioCryptocurrencyInstructionResponseDTO[];
};

export const CryptoSection: FC<Props> = ({ crypto }) => {
	const { t } = useResource();

	const recurringValue = (
		crypto: ClientPortfolioCryptocurrencyInstructionResponseDTO,
	) => {
		if (!crypto.countFrom || !crypto.currencyFrom) {
			return EMPTY_DETAIL_FIELD;
		}

		const frequency = t(
			Resources.PortfolioDetail.ToolHistory.Fund.Frequency[crypto.frequency],
		).toLowerCase();
		const formattedAmount = numberFormatter.format(crypto.countFrom);

		return `${formattedAmount} ${getCurrencySymbol(crypto.currencyFrom as CurrencyCode)} ${frequency}`;
	};

	const entryTransactionAmount = (
		crypto: ClientPortfolioCryptocurrencyInstructionResponseDTO,
	) => {
		if (!crypto.enterFee?.amount) {
			return EMPTY_DETAIL_FIELD;
		}

		const currency =
			crypto.investmentType === ClientPortfolioInvestmentTypeEnum.Buy
				? crypto.currencyFrom
				: crypto.currencyTo;

		const isPercent =
			crypto.enterFee.amountType ===
			ClientPortfolioInvestmentEnterFeeAmountTypeEnum.Percent;

		if (isPercent) {
			return `${numberFormatter.format(crypto.enterFee.amount)} %`;
		}

		return `${numberFormatter.format(crypto.enterFee.amount)} ${getCurrencySymbol(currency as CurrencyCode)}`;
	};

	const amount = (
		crypto: ClientPortfolioCryptocurrencyInstructionResponseDTO,
	): string => {
		if (!crypto.countTo) {
			return EMPTY_DETAIL_FIELD;
		}

		return String(numberFormatter.format(crypto.countTo));
	};

	return (
		<Stack>
			{crypto.map((crypto, index) => {
				// TODO: Keep as example if they decide to return behavior. Removed 07.11.2025
				// biome-ignore lint/correctness/noUnusedVariables: kept for potential future use
				const isActive = getIsActive(
					crypto.date,
					crypto.endedAt,
					crypto.frequency === ClientPortfolioInvestmentFrequencyEnum.Once,
				);

				const fundType = crypto.investmentType
					? t(
							Resources.PortfolioDetail.ToolHistory.ActionType[
								crypto.investmentType
							],
						)
					: EMPTY_DETAIL_FIELD;

				const entryTransaction = crypto.enterFee?.type
					? t(
							Resources.PortfolioDetail.ToolHistory.Fund.EntryTransaction[
								crypto.enterFee?.type
							],
						)
					: t(Resources.PortfolioDetail.ToolHistory.Fund.noFee);

				const fundApplying = crypto?.enterFee?.applying
					? t(
							Resources.PortfolioDetail.ToolHistory.Fund.Applying[
								crypto.enterFee?.applying
							],
						)
					: "";

				return (
					<DataRowCollapsible
						key={crypto.id}
						label={`${t(Resources.PortfolioDetail.ToolHistory.ActionType[crypto.investmentType ?? ClientPortfolioInvestmentTypeEnum.Buy])} - ${dateFormatter(crypto.date)}`}
						defaultExpanded={index === 0}
					>
						<DataRow
							label={t(Resources.PortfolioDetail.ToolHistory.Fund.type)}
							value={fundType}
						/>
						<DataRow
							label={t(
								Resources.PortfolioDetail.ToolHistory.Cryptocurrency
									.holdingType,
							)}
							value={t(
								Resources.PortfolioDetail.ToolHistory.Cryptocurrency
									.HoldingType[crypto.holdingType],
							)}
						/>
						<DataRow
							label={t(Resources.PortfolioDetail.ToolHistory.Fund.startDate)}
							value={dateFormatter(crypto.date)}
						/>
						<DataRow
							label={t(Resources.PortfolioDetail.ToolHistory.Fund.endDate)}
							value={dateFormatter(crypto.endedAt)}
						/>
						<DataRow
							label={t(Resources.PortfolioDetail.ToolHistory.Fund.amount)}
							value={recurringValue(crypto)}
						/>
						<DataRow
							label={t(
								Resources.PortfolioDetail.ToolHistory.Cryptocurrency.amount,
							)}
							value={amount(crypto)}
						/>
						{crypto.investmentType ===
							ClientPortfolioInvestmentTypeEnum.Buy && (
							<>
								{crypto.enterFee && (
									<DataRow
										label={t(
											Resources.PortfolioDetail.ToolHistory.Fund
												.entryTransaction,
										)}
										value={entryTransaction}
									/>
								)}
								<DataRow
									label={t(
										Resources.PortfolioDetail.ToolHistory.Fund
											.entryTransactionAmount,
									)}
									value={entryTransactionAmount(crypto)}
								/>
								{crypto.enterFee?.type ===
									ClientPortfolioInvestmentEnterFeeTypeEnum.Once &&
									crypto.frequency ===
										ClientPortfolioInvestmentFrequencyEnum.Once && (
										<DataRow
											label={t(
												Resources.PortfolioDetail.ToolHistory.Fund.applying,
											)}
											value={fundApplying}
										/>
									)}
							</>
						)}
						{crypto.enterFee?.chargedFeeFromFirstPayments && (
							<DataRow
								label={t(
									Resources.PortfolioDetail.ToolHistory.Fund
										.entryTransactionProcantage,
								)}
								value={`${crypto.enterFee?.chargedFeeFromFirstPayments} %`}
							/>
						)}
					</DataRowCollapsible>
				);
			})}
		</Stack>
	);
};
